<template>
    <v-form ref="form" v-model="valid" data-cy="guest-category-form" class="pb-4">
        <v-row class="mb-4 flex-wrap" dense>
            <v-col>
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col class="align-center">
                <v-row dense>
                    <v-switch
                        v-model="vehicle.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4 width-max-content"
                        :label="vehicle.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <TextFieldLocale
                    v-model="vehicle.name[activeLangCode]"
                    :label="`${$t('name')}*`"
                    :active-lang-code="activeLangCode"
                    data-cy="name"
                    :rules="[requiredRule('name')]"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <v-text-field
                    v-model="vehicle.maxCapacity"
                    type="number"
                    variant="outlined"
                    density="compact"
                    :label="`${$t('max_capacity')}*`"
                    hide-spin-buttons
                    max-width="200"
                    data-cy="max-capacity"
                    :rules="[requiredRule('max_capacity')]"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'

    const initVehicle = () => ({
        name: {},
        active: true,
        maxCapacity: null,
    })

    const props = defineProps<{
        editVehicleTypeId?: string | number | null
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()
    const router = useRouter()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const vehicle = ref(initVehicle())

    onMounted(async () => {
        if (props.editVehicleTypeId) await loadData()
        activeLangCode.value = app.$i18n.locale.value
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {id: props.editVehicleTypeId, locale: 'all'}
            const {
                vehicleTypes: [vehicleTypes],
            } = await app.$api.vehicleType.get(rq)
            vehicle.value = vehicleTypes
        } catch (e) {
            vehicle.value = initVehicle()
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            if (props.editVehicleTypeId) {
                await app.$api.vehicleType.put(props.editVehicleTypeId, {
                    ...vehicle.value,
                })
            } else {
                const res = await app.$api.vehicleType.post({
                    ...vehicle.value,
                })
                await router.push({name: 'vehicleType', params: {id: res.id}})
            }
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const cancel = () => {
        if (props.editVehicleTypeId) {
            router.push({name: 'vehicleTypes'})
        } else emit('cancel')
    }

    const localized = useLocalized()

    const useVehicleName = computed(() => {
        if (!vehicle.value?.name) return '...'
        return localized(vehicle.value.name)
    })

    const useVehicleMaxCapacity = computed(() => {
        return vehicle.value?.maxCapacity
    })

    defineExpose({
        useVehicleName,
        useVehicleMaxCapacity,
    })
</script>
